import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import apiService from '../services/apiService';
import API_BASE_URL from '../config/api';
import {
    Mail,
    Phone,
    Calendar,
    Shield,
    MapPin,
    CreditCard,
    Award,
    Save,
    Briefcase,
    Clock,
    User,
    CheckCircle,
    XCircle,
    Lock,
    FileText,
    Eye,
    EyeOff,
    Camera,
    Upload,
    ArrowLeftRight
} from 'lucide-react';

const Profile = () => {
    const { user, updateUserProfile, refreshUser } = useAuth();
    const { showAlert } = useAlert();
    const location = useLocation();
    const navigate = useNavigate();

    // Determine active tab based on URL
    const getActiveTab = () => {
        if (location.pathname === '/profile/kyc') return 'kyc';
        if (location.pathname === '/profile/bank') return 'kyc'; // Redirect bank to kyc
        if (location.pathname === '/profile/change-password') return 'password';
        return 'personal';
    };

    // Derived state for initial tab
    const [activeTab, setActiveTab] = useState(getActiveTab());

    // Sync tab with URL changes
    useEffect(() => {
        setActiveTab(getActiveTab());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleTabChange = (tab) => {
        const paths = {
            personal: '/profile',
            kyc: '/profile/kyc',
            password: '/profile/change-password'
        };
        navigate(paths[tab]);
    };

    // Check if profile is locked (KYC verified but edit access not granted)
    const isProfileLocked = Number(user?.is_verified) === 1 && Number(user?.can_edit_profile) === 0;

    const [isEditing, setIsEditing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Form States
    const [formData, setFormData] = useState({});
    const [binaryData, setBinaryData] = useState(null);

    // Initialize data from user context and fetch fresh profile details
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                // First initialize from context for immediate display
                if (user) {
                    const initialData = {
                        full_name: user.full_name || user.name || '',
                        user_id: user.user_id || '',
                        email: user.email || '',
                        mobile: user.mobile || '',
                        dob: user.dob || '',
                        address: user.address || '',
                        city: user.city || '',
                        state: user.state || '',
                        pincode: user.pincode || '',
                        country: user.country || '',
                        profile_image: user.profile_image || '',

                        pan_number: user.pan_number || '',
                        aadhaar_number: user.aadhaar_number || '',
                        kyc_status: user.kyc_status || 'Pending',

                        bank_name: user.bank_name || '',
                        account_number: user.account_number || '',
                        ifsc_code: user.ifsc_code || '',
                        branch_name: user.branch_name || '',
                        account_holder_name: user.account_holder_name || '',
                        upi_id: user.upi_id || '',

                        gender: user.gender || 'male',
                        marital_status: user.marital_status || 'single',
                        nominee_name: user.nominee_name || '',
                        nominee_relation: user.nominee_relation || '',

                        currentPassword: '',
                        newPassword: '',
                        confirmNewPassword: '',
                        currentPin: '',
                        newPin: '',
                        confirmNewPin: ''
                    };
                    setFormData(initialData);
                }

                // Then fetch fresh data from profile API
                const response = await apiService.getProfile();
                if (response.success && response.data?.user) {
                    const freshUser = response.data.user;
                    setFormData(prev => ({
                        ...prev,
                        full_name: freshUser.full_name || freshUser.name || '',
                        user_id: freshUser.user_id || prev.user_id,
                        email: freshUser.email || prev.email,
                        mobile: freshUser.mobile || prev.mobile,
                        dob: freshUser.dob || prev.dob,
                        address: freshUser.address || prev.address,
                        city: freshUser.city || prev.city,
                        state: freshUser.state || prev.state,
                        pincode: freshUser.pincode || prev.pincode,
                        country: freshUser.country || prev.country,
                        profile_image: freshUser.profile_image || prev.profile_image,

                        pan_number: freshUser.pan_number || prev.pan_number,
                        aadhaar_number: freshUser.aadhaar_number || prev.aadhaar_number,
                        kyc_status: freshUser.kyc_status || prev.kyc_status,

                        bank_name: freshUser.bank_name || prev.bank_name,
                        account_number: freshUser.account_number || prev.account_number,
                        ifsc_code: freshUser.ifsc_code || prev.ifsc_code,
                        branch_name: freshUser.branch_name || prev.branch_name,
                        account_holder_name: freshUser.account_holder_name || prev.account_holder_name,
                        upi_id: freshUser.upi_id || prev.upi_id,

                        gender: freshUser.gender || prev.gender,
                        marital_status: freshUser.marital_status || prev.marital_status,
                        nominee_name: freshUser.nominee_name || prev.nominee_name,
                        nominee_relation: freshUser.nominee_relation || prev.nominee_relation
                    }));
                }

                // Fetch Binary Income Data (Carry Forward)
                const binaryResp = await fetch(`${API_BASE_URL}/user/binary_income.php`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });
                const binaryResult = await binaryResp.json();
                if (binaryResult.success) {
                    setBinaryData(binaryResult.data);
                }
            } catch (err) {
                console.error("Error fetching profile details:", err);
            }
        };

        fetchProfileData();
    }, [user]);

    // Profile image upload handler
    const handleProfileImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showAlert('Image size should be less than 5MB', 'error');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64Image = reader.result;

                // If in editing mode, just update local state
                if (isEditing) {
                    setFormData(prev => ({ ...prev, profile_image: base64Image }));
                } else {
                    // Otherwise, upload immediately
                    setIsUploading(true);
                    try {
                        const result = await updateUserProfile({ profile_image: base64Image });
                        if (result.success) {
                            showAlert('Profile picture updated successfully!', 'success');
                            setFormData(prev => ({ ...prev, profile_image: base64Image }));
                        } else {
                            showAlert(result.message || 'Failed to update profile picture', 'error');
                        }
                    } catch (err) {
                        showAlert('An error occurred while uploading', 'error');
                    } finally {
                        setIsUploading(false);
                    }
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'full_name') {
            value = value.toUpperCase();
        }
        if (name === 'ifsc_code') {
            value = value.toUpperCase();
            if (value.length === 11) {
                handleIFSC(value);
            }
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleIFSC = async (code) => {
        if (!code || code.length !== 11) return;
        try {
            const response = await fetch(`https://ifsc.razorpay.com/${code}`);
            if (response.ok) {
                const data = await response.json();
                setFormData(prev => ({
                    ...prev,
                    bank_name: data.BANK,
                    branch_name: data.BRANCH,
                    city: prev.city || data.CITY,
                    state: prev.state || data.STATE
                }));
            }
        } catch (e) {
            console.error("IFSC Lookup Failed", e);
        }
    };

    const handleForgotPin = async () => {
        try {
            const response = await apiService.forgotTransactionPin();
            if (response.success) {
                showAlert(response.message || "Transaction PIN sent to your email!", 'success');
            } else {
                showAlert(response.message || "Failed to send PIN", 'error');
            }
        } catch (err) {
            showAlert(err.message || "An error occurred", 'error');
        }
    };

    const handleSave = async () => {
        if (activeTab === 'password') {
            // Handle Password Change
            if (formData.newPassword || formData.confirmNewPassword || formData.currentPassword) {
                if (!formData.currentPassword || !formData.newPassword) {
                    showAlert("Please fill all password fields", 'warning');
                    return;
                }
                if (formData.newPassword !== formData.confirmNewPassword) {
                    showAlert("New passwords do not match!", 'error');
                    return;
                }
                try {
                    const response = await apiService.changePassword(formData.currentPassword, formData.newPassword);
                    if (response.success) {
                        showAlert("Password changed successfully!", 'success');
                        setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmNewPassword: '' }));
                    } else {
                        showAlert(response.message || "Failed to change password", 'error');
                    }
                } catch (err) {
                    showAlert(err.message || "An error occurred", 'error');
                }
            }

            // Handle Transaction PIN Change
            if (formData.newPin || formData.confirmNewPin || formData.currentPin) {
                if (!formData.currentPin || !formData.newPin) {
                    showAlert("Please fill all PIN fields", 'warning');
                    return;
                }
                if (formData.newPin !== formData.confirmNewPin) {
                    showAlert("New PINs do not match!", 'error');
                    return;
                }
                if (!/^[0-9]{4}$/.test(formData.newPin)) {
                    showAlert("Transaction PIN must be exactly 4 digits!", 'error');
                    return;
                }
                try {
                    const response = await apiService.changeTransactionPin(formData.currentPin, formData.newPin);
                    if (response.success) {
                        showAlert("Transaction PIN updated successfully!", 'success');
                        setFormData(prev => ({ ...prev, currentPin: '', newPin: '', confirmNewPin: '' }));
                    } else {
                        showAlert(response.message || "Failed to update PIN", 'error');
                    }
                } catch (err) {
                    showAlert(err.message || "An error occurred", 'error');
                }
            }
            return;
        }

        // Handle KYC & Bank Details
        if (activeTab === 'kyc') {
            await handleKycAndBankSubmit();
            return;
        }

        // For other tabs, use standard profile update
        const result = await updateUserProfile(formData);
        if (result.success) {
            setIsEditing(false);
            showAlert("Profile updated successfully!", 'success');
        } else {
            showAlert(result.message || "Failed to update profile", 'error');
        }
    };

    const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
    const [showPin, setShowPin] = useState({ current: false, new: false, confirm: false });

    // Render Helpers
    const renderPersonal = () => (
        <div className="space-y-6">
            {/* Profile Picture Upload */}
            <div className="flex flex-col items-center pb-6 border-b border-gray-100">
                <div className="relative">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg border-4 border-white">
                        {isUploading ? (
                            <div className="flex items-center justify-center h-full w-full bg-black/20">
                                <svg className="animate-spin h-8 w-8 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                        ) : formData.profile_image ? (
                            <img
                                src={formData.profile_image.startsWith('data:') ? formData.profile_image : (formData.profile_image.startsWith('http') ? formData.profile_image : `${window.location.origin}${formData.profile_image.startsWith('/') ? '' : '/'}${formData.profile_image}`)}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<span class="text-4xl font-bold text-white">${formData.full_name?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                                }}
                            />
                        ) : (
                            <span className="text-4xl font-bold text-white">
                                {formData.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                        )}
                    </div>
                    <label className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200 group">
                        <Camera size={20} className="text-blue-600 group-hover:scale-110 transition-transform" />
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            onChange={handleProfileImageChange}
                            className="hidden"
                        />
                    </label>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">{formData.full_name || 'User'}</h3>
                <p className="text-sm text-gray-500">@{formData.user_id || 'NX000000'}</p>
                {(!isProfileLocked) && (
                    <p className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                        <Upload size={12} /> Click camera icon to {isEditing ? 'change' : 'fast upload'} profile picture (Max 5MB)
                    </p>
                )}
            </div>

            {/* Team PV Status (Carry Forward) */}
            {binaryData && (
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-2xl p-6 mb-6">
                    <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <ArrowLeftRight size={16} /> Team PV Status (Carry Forward)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-xl border border-indigo-200 shadow-sm">
                            <p className="text-xs text-indigo-600 font-semibold mb-1">Left Carry Forward</p>
                            <p className="text-2xl font-bold text-gray-900">{parseFloat(binaryData.left_carry_forward).toFixed(2)} PV</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm">
                            <p className="text-xs text-blue-600 font-semibold mb-1">Right Carry Forward</p>
                            <p className="text-2xl font-bold text-gray-900">{parseFloat(binaryData.right_carry_forward).toFixed(2)} PV</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            name="full_name"
                            value={formData.full_name || ''}
                            onChange={handleChange}
                            disabled
                            className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            onChange={handleChange}
                            disabled
                            className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                    <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile || ''}
                            onChange={handleChange}
                            disabled
                            className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="date"
                            name="dob"
                            value={formData.dob || ''}
                            onChange={handleChange}
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            min="1920-01-01"
                            disabled
                            className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <select
                        name="gender"
                        value={formData.gender || 'male'}
                        onChange={handleChange}
                        disabled={isProfileLocked}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed text-sm font-semibold"
                    >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status</label>
                    <select
                        name="marital_status"
                        value={formData.marital_status || 'single'}
                        onChange={handleChange}
                        disabled={isProfileLocked}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed text-sm font-semibold"
                    >
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                    </select>
                </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Address</label>
                        <div className="relative">
                            <MapPin className="absolute left-3 top-3 text-gray-400" size={20} />
                            <textarea
                                name="address"
                                value={formData.address || ''}
                                onChange={handleChange}
                                disabled={!isEditing}
                                rows="3"
                                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed resize-none"
                            ></textarea>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input
                            type="text"
                            name="city"
                            value={formData.city || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                        <input
                            type="text"
                            name="state"
                            value={formData.state || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                        <input
                            type="text"
                            name="pincode"
                            value={formData.pincode || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nominee Name</label>
                        <input
                            type="text"
                            name="nominee_name"
                            value={formData.nominee_name || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nominee Relation</label>
                        <input
                            type="text"
                            name="nominee_relation"
                            value={formData.nominee_relation || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const [aadhaarFile, setAadhaarFile] = useState(null);
    const [panFile, setPanFile] = useState(null);
    const [aadhaarError, setAadhaarError] = useState('');
    const [panError, setPanError] = useState('');
    const [bankError, setBankError] = useState('');
    const [kycLoading, setKycLoading] = useState(false);
    const [aadhaarChecking, setAadhaarChecking] = useState(false);
    const [bankChecking, setBankChecking] = useState(false);
    const [passbookFile, setPassbookFile] = useState(null);

    // Validation patterns
    const AADHAAR_PATTERN = /^[2-9]{1}[0-9]{11}$/;
    const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    // Check Aadhaar in database
    const checkAadhaarInDb = async (value) => {
        if (!value || value.length !== 12) return;
        if (!AADHAAR_PATTERN.test(value)) {
            setAadhaarError('Invalid Aadhaar. Cannot start with 0 or 1.');
            return;
        }

        setAadhaarChecking(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=aadhaar&value=${value}&user_id=${user?.id || ''}`);
            const data = await response.json();
            if (data.exists) {
                setAadhaarError('❌ ' + (data.message || 'Aadhaar already registered'));
            } else {
                setAadhaarError('');
            }
        } catch (e) {
            console.error('Aadhaar check failed', e);
        } finally {
            setAadhaarChecking(false);
        }
    };

    // Check PAN in database
    const checkPanInDb = async (value) => {
        if (!value || value.length !== 10) return;
        if (!PAN_PATTERN.test(value)) {
            setPanError('Invalid PAN format. Example: ABCDE1234F');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=pan&value=${value}&user_id=${user?.id || ''}`);
            const data = await response.json();
            if (data.exists) {
                setPanError('❌ ' + (data.message || 'PAN already registered'));
            } else {
                setPanError('');
            }
        } catch (e) {
            console.error('PAN check failed', e);
        }
    };

    // Check Bank Account in database
    const checkBankInDb = async (value) => {
        if (!value || value.length < 8) return;

        setBankChecking(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=bank&value=${value}&user_id=${user?.id || ''}`);
            const data = await response.json();
            if (data.exists) {
                setBankError('❌ ' + (data.message || 'Bank account already registered'));
            } else {
                setBankError('');
            }
        } catch (e) {
            console.error('Bank check failed', e);
        } finally {
            setBankChecking(false);
        }
    };

    const handleAadhaarChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 12);
        setFormData({ ...formData, aadhaar_number: value });
        // Clear error while typing
        if (aadhaarError && !aadhaarError.includes('already')) {
            setAadhaarError('');
        }
        // Format validation
        if (value && value.length === 12 && !AADHAAR_PATTERN.test(value)) {
            setAadhaarError('Invalid Aadhaar. Cannot start with 0 or 1.');
        }
    };

    const handlePanChange = (e) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        setFormData({ ...formData, pan_number: value });
        // Clear error while typing
        if (panError && !panError.includes('already')) {
            setPanError('');
        }
        // Format validation
        if (value && value.length === 10 && !PAN_PATTERN.test(value)) {
            setPanError('Invalid PAN format. Example: ABCDE1234F');
        }
    };

    const handleBankAccountChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 18);
        setFormData({ ...formData, account_number: value });
        // Clear error while typing
        if (bankError) setBankError('');
    };





    const handleKycAndBankSubmit = async () => {
        // Validate KYC fields
        if (!formData.aadhaar_number || formData.aadhaar_number.length !== 12) {
            showAlert('Please enter a valid 12-digit Aadhaar number', 'error');
            return;
        }
        if (aadhaarError && aadhaarError.includes('already')) {
            showAlert('Aadhaar number is already registered', 'error');
            return;
        }

        // Validate Bank fields
        if (!formData.account_holder_name || !formData.account_holder_name.trim()) {
            showAlert('Please enter the Account Holder Name', 'error');
            return;
        }
        if (!formData.account_number || formData.account_number.length < 8) {
            showAlert('Please enter a valid Account Number', 'error');
            return;
        }
        if (!formData.ifsc_code || formData.ifsc_code.length !== 11) {
            showAlert('Please enter a valid IFSC Code', 'error');
            return;
        }

        // Validate Files for Initial Submission
        const isNewKyc = !user?.aadhaar_number;
        const isNewBank = !user?.account_number;

        if (isNewKyc && !aadhaarFile) {
            showAlert('Please upload Aadhaar Card image', 'error');
            return;
        }
        if (isNewBank && !passbookFile) {
            showAlert('Please upload Passbook / Cancelled Cheque image', 'error');
            return;
        }

        setKycLoading(true);
        try {
            // 1. Update Profile Text Details (Aadhaar, PAN, Bank Details)
            const profileResult = await updateUserProfile(formData);
            
            if (profileResult.success) {
                // 2. Upload Documents if any are selected
                if (aadhaarFile || panFile || passbookFile) {
                    const formDataToSend = new FormData();
                    if (aadhaarFile) formDataToSend.append('aadhaar_document', aadhaarFile);
                    if (panFile) formDataToSend.append('pan_document', panFile);
                    if (passbookFile) formDataToSend.append('passbook_document', passbookFile);
                    
                    // Add identification fields for the KYC endpoint
                    formDataToSend.append('aadhaar_number', formData.aadhaar_number);
                    formDataToSend.append('account_number', formData.account_number);

                    const kycResult = await apiService.submitKYC(formDataToSend);
                    if (kycResult.success) {
                        setAadhaarFile(null);
                        setPanFile(null);
                        setPassbookFile(null);
                        showAlert('KYC and Bank details submitted successfully! Verification pending.', 'success');
                    } else {
                        showAlert(kycResult.message || 'Details updated but document upload failed', 'warning');
                    }
                } else {
                    showAlert('Details updated successfully!', 'success');
                }
                
                setIsEditing(false);
                await refreshUser();
            } else {
                showAlert(profileResult.message || 'Failed to update details', 'error');
            }
        } catch (err) {
            showAlert(err.message || 'An error occurred during submission', 'error');
        } finally {
            setKycLoading(false);
        }
    };

    const renderKYCAndBank = () => (
        <div className="space-y-8">
            {/* KYC Status Banner */}
            <div className={`p-4 rounded-xl flex items-start gap-4 ${formData.kyc_status === 'Verified' ? 'bg-green-50 text-green-700 border border-green-200' :
                formData.kyc_status === 'Rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                    'bg-yellow-50 text-yellow-700 border border-yellow-200'
                }`}>
                {formData.kyc_status === 'Verified' ? <Shield className="mt-1" /> :
                    formData.kyc_status === 'Rejected' ? <XCircle className="mt-1" /> :
                        <Clock className="mt-1" />}
                <div>
                    <h4 className="font-semibold">Verification Status: {formData.kyc_status || 'Pending'}</h4>
                    <p className="text-sm mt-1 opacity-90">
                        {formData.kyc_status === 'Verified'
                            ? 'Your identity and bank accounts are verified. You can withdraw funds.'
                            : formData.kyc_status === 'Rejected'
                                ? (
                                    <>
                                        <span className="font-bold">Reason:</span> {formData.kyc_rejection_reason || 'Information provided was invalid.'} 
                                        <br />
                                        Please re-upload valid documents.
                                    </>
                                )
                                : 'Please complete your KYC and Bank details to enable withdrawals.'}
                    </p>
                </div>
            </div>

            {/* Bank Account Details Section */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-purple-50 px-6 py-4 border-b border-purple-100 flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                        <CreditCard size={20} />
                    </div>
                    <h3 className="font-bold text-gray-900 border-none!">Bank Account Details</h3>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder Name</label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    name="account_holder_name"
                                    value={formData.account_holder_name || ''}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                            <div className="relative">
                                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    name="bank_name"
                                    value={formData.bank_name || ''}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                            <div className="relative">
                                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    name="account_number"
                                    value={formData.account_number || ''}
                                    onChange={handleBankAccountChange}
                                    onBlur={() => checkBankInDb(formData.account_number)}
                                    disabled={!isEditing}
                                    className={`w-full pl-10 pr-10 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 ${bankError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                                />
                                {bankChecking && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                            {bankError && <p className="text-xs text-red-500 mt-1">{bankError}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                            <input
                                type="text"
                                name="ifsc_code"
                                value={formData.ifsc_code || ''}
                                onChange={handleChange}
                                disabled={!isEditing}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase disabled:opacity-70 font-semibold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Branch Name</label>
                            <input
                                type="text"
                                name="branch_name"
                                value={formData.branch_name || ''}
                                onChange={handleChange}
                                disabled={!isEditing}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 font-semibold"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Passbook / Cancelled Cheque <span className="text-red-500">*</span></label>
                        <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all ${passbookFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-purple-400 bg-gray-50'
                            } ${!isEditing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => e.target.files[0] && setPassbookFile(e.target.files[0])}
                                disabled={!isEditing}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            />
                            <div className="text-center">
                                {passbookFile ? (
                                    <div className="flex flex-col items-center gap-2 text-green-700">
                                        <CheckCircle size={32} />
                                        <span className="font-semibold">{passbookFile.name}</span>
                                        <p className="text-xs opacity-70">Click or drag to replace</p>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 flex flex-col items-center gap-2">
                                        <Upload size={32} />
                                        <p className="font-medium">Upload Bank Passbook Image</p>
                                        <p className="text-xs">JPG, PNG or PDF (Max 2MB)</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500 italic">
                            Note: Passbook pic is required for bank verification. Please ensure all details like Account No. and IFSC are clearly visible.
                        </p>
                    </div>
                </div>
            </div>

            {/* KYC Documents Section */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-blue-50 px-6 py-4 border-b border-blue-100 flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <Shield size={20} />
                    </div>
                    <h3 className="font-bold text-gray-900 border-none!">KYC Verification</h3>
                </div>
                <div className="p-6 space-y-6">
                    {/* Aadhaar Section */}
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Aadhaar Number <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="aadhaar_number"
                                        value={formData.aadhaar_number || ''}
                                        onChange={handleAadhaarChange}
                                        onBlur={() => checkAadhaarInDb(formData.aadhaar_number)}
                                        disabled={!isEditing}
                                        maxLength="12"
                                        placeholder="Enter 12-digit Aadhaar"
                                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 ${aadhaarError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                                    />
                                    {aadhaarChecking && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                {aadhaarError && <p className="text-xs text-red-500 mt-1">{aadhaarError}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Aadhaar Card</label>
                                <div className={`relative border border-dashed rounded-lg p-3 transition-all ${aadhaarFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 bg-white'
                                    } ${!isEditing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,application/pdf"
                                        onChange={(e) => e.target.files[0] && setAadhaarFile(e.target.files[0])}
                                        disabled={!isEditing}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Upload size={16} />
                                        <span>{aadhaarFile ? aadhaarFile.name : 'Upload Aadhaar Card'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PAN Section */}
                    <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number (Optional)</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="pan_number"
                                        value={formData.pan_number || ''}
                                        onChange={handlePanChange}
                                        onBlur={() => checkPanInDb(formData.pan_number)}
                                        disabled={!isEditing}
                                        maxLength="10"
                                        placeholder="Enter PAN Number"
                                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 uppercase disabled:opacity-70 ${panError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}
                                    />
                                </div>
                                {panError && <p className="text-xs text-red-500 mt-1">{panError}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Upload PAN Card</label>
                                <div className={`relative border border-dashed rounded-lg p-3 transition-all ${panFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-amber-400 bg-white'
                                    } ${!isEditing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,application/pdf"
                                        onChange={(e) => e.target.files[0] && setPanFile(e.target.files[0])}
                                        disabled={!isEditing}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <Upload size={16} />
                                        <span>{panFile ? panFile.name : 'Upload PAN Card'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Consolidated Submit Button */}
            {isEditing && (
                <div className="flex flex-col items-center gap-3 pt-6 border-t border-gray-100">
                    <button
                        onClick={handleKycAndBankSubmit}
                        disabled={kycLoading || !formData.aadhaar_number || !formData.account_number || aadhaarError || bankError}
                        className="w-full md:w-auto px-12 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-3"
                    >
                        {kycLoading ? (
                            <>
                                <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <Shield size={24} />
                                <span>Submit for Verification</span>
                            </>
                        )}
                    </button>
                    <p className="text-xs text-gray-500 text-center max-w-md">
                        By submitting, you agree that the information provided is accurate. 
                        Verification usually takes 2-4 hours. You'll be notified of the status.
                    </p>
                </div>
            )}
        </div>
    );

    const renderPassword = () => (
        <div className="max-w-md mx-auto space-y-6 py-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type={showPassword.current ? "text" : "password"}
                        name="currentPassword"
                        value={formData.currentPassword || ''}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type={showPassword.new ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword || ''}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type={showPassword.confirm ? "text" : "password"}
                        name="confirmNewPassword"
                        value={formData.confirmNewPassword || ''}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
            </div>
            <div className="pt-4">
                <button
                    onClick={handleSave}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all"
                >
                    <Save size={20} />
                    Update Password
                </button>
            </div>

            <hr className="my-8 border-gray-200" />

            {/* Transaction PIN Section */}
            <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield size={20} className="text-orange-500" /> Transaction PIN
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current PIN</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type={showPin.current ? "text" : "password"}
                                name="currentPin"
                                value={formData.currentPin || ''}
                                onChange={handleChange}
                                maxLength="4"
                                className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPin(prev => ({ ...prev, current: !prev.current }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPin.current ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <div className="flex justify-end mt-1">
                            <button
                                type="button"
                                onClick={handleForgotPin}
                                className="text-xs text-orange-600 hover:text-orange-800 font-semibold"
                            >
                                Forgot PIN?
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">New PIN (4 Digits)</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type={showPin.new ? "text" : "password"}
                                name="newPin"
                                value={formData.newPin || ''}
                                onChange={handleChange}
                                maxLength="4"
                                className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPin(prev => ({ ...prev, new: !prev.new }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPin.new ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New PIN</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type={showPin.confirm ? "text" : "password"}
                                name="confirmNewPin"
                                value={formData.confirmNewPin || ''}
                                onChange={handleChange}
                                maxLength="4"
                                className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPin(prev => ({ ...prev, confirm: !prev.confirm }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPin.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>
                    <div className="pt-2">
                        <button
                            onClick={handleSave}
                            className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all"
                        >
                            <Save size={20} />
                            Update Transaction PIN
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <Layout>
            <div className="max-w-5xl mx-auto pb-12">
                {/* Locked Profile Warning */}
                {isProfileLocked && activeTab !== 'password' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                        <div className="bg-amber-100 rounded-full p-2">
                            <Shield className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-amber-800">Profile Locked</h3>
                            <p className="text-sm text-amber-700">Your profile is locked after KYC verification. Contact admin to request edit access.</p>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                        <p className="text-gray-500">Manage your account details and settings</p>
                    </div>
                    {activeTab !== 'password' && (
                        isProfileLocked ? (
                            <div className="px-6 py-2.5 rounded-xl font-semibold flex items-center gap-2 bg-gray-400 text-white cursor-not-allowed">
                                <Lock size={18} />
                                Editing Disabled
                            </div>
                        ) : (
                            <button
                                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                                className={`px-6 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all ${isEditing
                                    ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-200'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200'
                                    }`}
                            >
                                {isEditing ? <Save size={18} /> : <FileText size={18} />}
                                {isEditing ? 'Save Changes' : 'Edit Profile'}
                            </button>
                        )
                    )}
                </div>

                {/* Tab Navigation - Scrollable on mobile */}
                <div className="flex bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5 mb-8 overflow-x-auto no-scrollbar scroll-smooth">
                    <div className="flex min-w-full sm:min-w-0">
                        <button
                            onClick={() => handleTabChange('personal')}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'personal' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            <User size={18} /> <span className="hidden xs:inline">Personal</span>
                        </button>
                        <button
                            onClick={() => handleTabChange('kyc')}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'kyc' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            <Shield size={18} /> <span className="hidden xs:inline">KYC & Bank Details</span>
                        </button>
                        <button
                            onClick={() => handleTabChange('password')}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'password' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            <Lock size={18} /> <span className="hidden xs:inline">Security</span>
                        </button>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 md:p-8">
                        {activeTab === 'personal' && (
                            <>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                                        <User size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Personal Information</h2>
                                        <p className="text-sm text-gray-500">Update your basic profile details</p>
                                    </div>
                                </div>
                                {renderPersonal()}
                            </>
                        )}

                        {activeTab === 'kyc' && (
                            <>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
                                        <Shield size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">KYC & Bank Details</h2>
                                        <p className="text-sm text-gray-500">Submit your verification documents and bank info</p>
                                    </div>
                                </div>
                                {renderKYCAndBank()}
                            </>
                        )}

                        {activeTab === 'password' && (
                            <>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                                        <Lock size={24} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Security</h2>
                                        <p className="text-sm text-gray-500">Update your account password</p>
                                    </div>
                                </div>
                                {renderPassword()}
                            </>
                        )}
                    </div>
                </div>

                {/* Profile Summary Footer */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-900 rounded-xl p-6 text-white bg-opacity-90">
                        <div className="flex items-center gap-3 mb-2">
                            <Award className="text-yellow-400" />
                            <h3 className="font-semibold">Current Rank</h3>
                        </div>
                        <p className="text-2xl font-bold">{user?.rank || 'Member'}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-2 text-gray-600">
                            <Calendar size={20} />
                            <h3 className="font-semibold">Join Date</h3>
                        </div>
                        <p className="text-xl font-bold text-gray-900">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-2 text-gray-600">
                            <CheckCircle size={20} className="text-green-500" />
                            <h3 className="font-semibold">Account Status</h3>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${user?.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            <p className="text-xl font-bold text-gray-900 capitalize">{user?.status || 'Active'}</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Profile;
